"use client"

import { useState } from "react"
import { Camera, Eye, AlertTriangle, CheckCircle, X, Footprints, Bomb } from "lucide-react"
import { formatDistanceToNow } from "date-fns"

const VibrationAnalysis = ({ device, alerts }) => {
  const [selectedImage, setSelectedImage] = useState(null)
  const [showImageModal, setShowImageModal] = useState(false)

  // Get vibration-related alerts for this device
  const vibrationAlerts = alerts.filter((alert) => alert.deviceId === device.id && alert.type === "vibration")

  // Generate mock image URL for demonstration
  const generateMockImageUrl = (imageId) => {
    const mockImages = [
      "https://images.unsplash.com/photo-1574144611937-0df059b5ef3e?w=400&h=300&fit=crop", // Animal
      "https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=400&h=300&fit=crop", // Person
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop", // Landscape
    ]
    return mockImages[Math.floor(Math.random() * mockImages.length)]
  }

  const getVibrationTypeIcon = (type) => {
    switch (type) {
      case "foot":
        return <Footprints className="h-5 w-5 text-yellow-600" />
      case "bomb":
        return <Bomb className="h-5 w-5 text-red-600" />
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-600" />
    }
  }

  const getVibrationTypeColor = (type) => {
    switch (type) {
      case "foot":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "bomb":
        return "bg-red-100 text-red-800 border-red-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const handleImageView = (alert) => {
    if (alert.cameraCapture) {
      setSelectedImage({
        ...alert.cameraCapture,
        url: generateMockImageUrl(alert.cameraCapture.imageId),
        alert: alert,
      })
      setShowImageModal(true)
    }
  }

  const simulateImageAnalysis = (imageId) => {
    // Simulate AI analysis results
    const analysisResults = [
      { detectedEntity: "Animal (Deer)", confidence: 92, threat: false },
      { detectedEntity: "Human (Suspicious)", confidence: 87, threat: true },
      { detectedEntity: "Animal (Dog)", confidence: 95, threat: false },
      { detectedEntity: "Human (Normal)", confidence: 78, threat: false },
    ]
    return analysisResults[Math.floor(Math.random() * analysisResults.length)]
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="card-title">Vibration Analysis - {device.name}</h3>
            <p className="card-subtitle">Advanced vibration detection and camera verification</p>
          </div>
          <div className="flex items-center space-x-2">
            <Camera className={`h-5 w-5 ${device.camera.status === "active" ? "text-green-500" : "text-gray-400"}`} />
            <span className="text-sm text-gray-600">Camera {device.camera.status}</span>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Vibration Thresholds */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-yellow-50 rounded-md p-4 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Footprints className="h-5 w-5 text-yellow-600 mr-2" />
                <span className="font-medium text-yellow-800">Foot Vibration</span>
              </div>
              <span className="text-sm text-yellow-600">
                Threshold: {device.sensors.vibration.footVibrationThreshold} Hz
              </span>
            </div>
            <p className="text-xs text-yellow-700 mt-1">Triggers camera capture for animal/human verification</p>
          </div>

          <div className="bg-red-50 rounded-md p-4 border border-red-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Bomb className="h-5 w-5 text-red-600 mr-2" />
                <span className="font-medium text-red-800">Bomb Vibration</span>
              </div>
              <span className="text-sm text-red-600">
                Threshold: {device.sensors.vibration.bombVibrationThreshold} Hz
              </span>
            </div>
            <p className="text-xs text-red-700 mt-1">High-frequency explosive signature detection</p>
          </div>
        </div>

        {/* Recent Vibration Events */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Recent Vibration Events</h4>

          {vibrationAlerts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No vibration events detected</p>
            </div>
          ) : (
            <div className="space-y-4">
              {vibrationAlerts.map((alert) => (
                <div key={alert.id} className="border border-gray-200 rounded-md p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {getVibrationTypeIcon(alert.subType)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getVibrationTypeColor(alert.subType)}`}
                          >
                            {alert.subType?.toUpperCase() || "UNKNOWN"} VIBRATION
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 mb-2">{alert.message}</p>

                        {alert.vibrationData && (
                          <div className="grid grid-cols-3 gap-4 text-xs text-gray-600 mb-2">
                            <div>
                              <span className="font-medium">Pattern:</span> {alert.vibrationData.pattern}
                            </div>
                            <div>
                              <span className="font-medium">Frequency:</span> {alert.vibrationData.frequency}
                            </div>
                            <div>
                              <span className="font-medium">Duration:</span> {alert.vibrationData.duration}
                            </div>
                          </div>
                        )}

                        {alert.cameraCapture && (
                          <div className="bg-blue-50 rounded-md p-3 mt-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-blue-900">Camera Capture Available</p>
                                <p className="text-xs text-blue-700">Image ID: {alert.cameraCapture.imageId}</p>
                                <p className="text-xs text-blue-700">Status: {alert.cameraCapture.analysisStatus}</p>
                              </div>
                              <button
                                onClick={() => handleImageView(alert)}
                                className="btn btn-primary btn-sm flex items-center"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View Image
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Camera Capture Analysis</h3>
              <button onClick={() => setShowImageModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <img
                  src={selectedImage.url || "/placeholder.svg"}
                  alt="Camera capture"
                  className="w-full h-64 object-cover rounded-md border border-gray-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Image ID</p>
                  <p className="text-sm text-gray-900">{selectedImage.imageId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Capture Time</p>
                  <p className="text-sm text-gray-900">{new Date(selectedImage.timestamp).toLocaleString()}</p>
                </div>
              </div>

              {selectedImage.analysisStatus === "pending" && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Analysis Pending</p>
                      <p className="text-xs text-yellow-700">AI analysis in progress...</p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      const analysis = simulateImageAnalysis(selectedImage.imageId)
                      setSelectedImage((prev) => ({
                        ...prev,
                        analysisStatus: "completed",
                        detectedEntity: analysis.detectedEntity,
                        confidence: analysis.confidence,
                        threat: analysis.threat,
                      }))
                    }}
                    className="btn btn-primary btn-sm mt-3"
                  >
                    Simulate Analysis
                  </button>
                </div>
              )}

              {selectedImage.analysisStatus === "completed" && (
                <div
                  className={`border rounded-md p-4 ${selectedImage.threat ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}
                >
                  <div className="flex items-center mb-2">
                    {selectedImage.threat ? (
                      <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    )}
                    <p className={`text-sm font-medium ${selectedImage.threat ? "text-red-800" : "text-green-800"}`}>
                      Analysis Complete
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Detected Entity</p>
                      <p className="text-sm text-gray-900">{selectedImage.detectedEntity}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Confidence</p>
                      <p className="text-sm text-gray-900">{selectedImage.confidence}%</p>
                    </div>
                  </div>

                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Threat Assessment</p>
                    <p className={`text-sm ${selectedImage.threat ? "text-red-800" : "text-green-800"}`}>
                      {selectedImage.threat ? "POTENTIAL THREAT DETECTED" : "NO THREAT DETECTED"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VibrationAnalysis
